import { useMemo, useState } from 'react';
import {
  AutocompleteField,
  Button,
  Dialog,
  NumberInput,
  SegmentedControl,
  Select,
  SelectItem,
  TextArea,
  TextField,
} from '@protoss/ui';
import {
  DEFAULT_LANDSCAPE_HUB_NAME,
  INTEGRATION_DIRECTIONS,
  TECH_STACK_CATEGORIES,
  TECH_STACK_HUB_ID,
  TECH_STACK_PLANS,
  TECH_STACK_ROW_MAPPING,
  TECH_STACK_STANDARD_INTEGRATION_HOURS,
  defaultInScopeForPlan,
  integrationDirectionLabel,
  planLabel,
  type IntegrationDirection,
  type IntegrationType,
  type SalesDealTemplateFieldSummary,
  type TechStackPlan,
  type TechStackSystem,
} from '@protoss/shared';
import type {
  QuestionnaireAnswerMap,
  QuestionnaireFieldLookup,
} from '../../../features/sales/utils/questionnaire-field-visibility';
import {
  TECH_STACK_SYSTEMS_FIELD_KEY,
  resolveTechStackSystemsFromAnswers,
} from './techStackLandscapeUtils';

interface TechStackEditorProps {
  /** The `tech_stack_systems` template field (drives the answer key). Null falls back to the field key. */
  field: SalesDealTemplateFieldSummary | null;
  answers: QuestionnaireAnswerMap;
  fieldLookup?: QuestionnaireFieldLookup;
  onChange: (answerKey: string, value: unknown) => void;
  disabled?: boolean;
  /** Central platform (new-system) label, the default integration target. Matches the diagram hub. */
  hubName?: string;
}

/** category id -> legacy `tech_stack_<cat>_tool` field key (the autocomplete option source). */
const TOOL_KEY_BY_CATEGORY = new Map(TECH_STACK_ROW_MAPPING.map((r) => [r.categoryId, r.toolKey]));

function newSystemId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `ts-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
}

function standardIntegration() {
  return {
    type: 'standard' as IntegrationType,
    hours: TECH_STACK_STANDARD_INTEGRATION_HOURS,
    direction: 'inbound' as IntegrationDirection,
  };
}

/**
 * Current Technology Stack editor. Replaces the legacy per-category tool/plan fields with a
 * repeatable list: each category can hold multiple technologies, each carries a plan, an
 * in/out-of-scope toggle (defaulted from the plan, overridable), and - when in scope - an
 * integration spec (Standard/Custom + hours + direction + notes). Writes the whole structured
 * `tech_stack_systems` answer; the estimate, SOW, and Future State diagram read it.
 */
export function TechStackEditor({
  field,
  answers,
  fieldLookup,
  onChange,
  disabled = false,
  hubName = DEFAULT_LANDSCAPE_HUB_NAME,
}: TechStackEditorProps) {
  const answerKey =
    field?.templateFieldId != null
      ? String(field.templateFieldId)
      : (field?.fieldKey ?? TECH_STACK_SYSTEMS_FIELD_KEY);

  // Display systems: the saved structured answer, or - when empty - proposals seeded from the
  // legacy AI-detected tool/plan answers. The seed is DISPLAY-ONLY (never written on mount), so
  // opening the section does not mark it dirty. The first real edit materializes the full list
  // into the answer (every edit handler commits the current `systems`).
  const systems = useMemo(
    () => resolveTechStackSystemsFromAnswers(answers, fieldLookup),
    [answers, fieldLookup]
  );

  // Per-category autocomplete options, sourced from the seeded legacy tool field options.
  const optionsByCategory = useMemo(() => {
    const map: Record<string, string[]> = {};
    for (const category of TECH_STACK_CATEGORIES) {
      const toolKey = TOOL_KEY_BY_CATEGORY.get(category.id);
      const options = toolKey ? (fieldLookup?.get(toolKey)?.options ?? []) : [];
      map[category.id] = options.map((option) => option.label);
    }
    return map;
  }, [fieldLookup]);

  // All named systems - the pool of flow-target options (the hub is prepended per row, self excluded).
  const namedSystems = useMemo(
    () => systems.filter((system) => system.name.trim().length > 0),
    [systems]
  );

  const [deleteTarget, setDeleteTarget] = useState<TechStackSystem | null>(null);
  const [confirmText, setConfirmText] = useState('');

  const commit = (next: TechStackSystem[]) => onChange(answerKey, next);

  const updateSystem = (id: string, updater: (system: TechStackSystem) => TechStackSystem) => {
    commit(systems.map((system) => (system.id === id ? updater(system) : system)));
  };

  const removeSystem = (id: string) => commit(systems.filter((system) => system.id !== id));

  const addSystem = (category: string) => {
    commit([
      ...systems,
      { id: newSystemId(), category, name: '', plan: 'keep', inScope: true, integration: standardIntegration() },
    ]);
  };

  const requestDelete = (system: TechStackSystem) => {
    // Nothing to confirm for an unnamed (just-added) row - drop it immediately.
    if (!system.name.trim()) {
      removeSystem(system.id);
      return;
    }
    setDeleteTarget(system);
    setConfirmText('');
  };

  const closeDelete = () => {
    setDeleteTarget(null);
    setConfirmText('');
  };

  const confirmDelete = () => {
    if (deleteTarget) removeSystem(deleteTarget.id);
    closeDelete();
  };

  const setPlan = (system: TechStackSystem, plan: TechStackPlan) => {
    const inScope = defaultInScopeForPlan(plan);
    updateSystem(system.id, (s) => ({
      ...s,
      plan,
      inScope,
      integration: inScope ? (s.integration ?? standardIntegration()) : undefined,
    }));
  };

  const setInScope = (system: TechStackSystem, inScope: boolean) => {
    updateSystem(system.id, (s) => ({
      ...s,
      inScope,
      integration: inScope ? (s.integration ?? standardIntegration()) : undefined,
    }));
  };

  const setIntegrationType = (system: TechStackSystem, type: IntegrationType) => {
    updateSystem(system.id, (s) => ({
      ...s,
      integration: {
        ...(s.integration ?? standardIntegration()),
        type,
        // Standard prefills the default; Custom keeps whatever is there for the user to fill.
        hours: type === 'standard' ? TECH_STACK_STANDARD_INTEGRATION_HOURS : (s.integration?.hours ?? 0),
      },
    }));
  };

  const setHours = (system: TechStackSystem, hours: number) => {
    updateSystem(system.id, (s) => ({
      ...s,
      integration: { ...(s.integration ?? standardIntegration()), hours: Math.max(0, hours) },
    }));
  };

  const setDirection = (system: TechStackSystem, direction: IntegrationDirection) => {
    updateSystem(system.id, (s) => ({
      ...s,
      integration: { ...(s.integration ?? standardIntegration()), direction },
    }));
  };

  const setFlowTarget = (system: TechStackSystem, target: string) => {
    updateSystem(system.id, (s) => ({
      ...s,
      integration: {
        ...(s.integration ?? standardIntegration()),
        // Store undefined for the hub (the default) to keep answers clean + back-compatible.
        flowTarget: target === TECH_STACK_HUB_ID ? undefined : target,
      },
    }));
  };

  const setNotes = (system: TechStackSystem, notes: string) => {
    updateSystem(system.id, (s) => ({
      ...s,
      integration: { ...(s.integration ?? standardIntegration()), notes: notes || undefined },
    }));
  };

  const setName = (system: TechStackSystem, name: string) => {
    updateSystem(system.id, (s) => ({ ...s, name }));
  };

  // Flow-target dropdown: the hub (default) plus every other named system, refreshed as tools
  // are added/renamed. A stale target (system since deleted) falls back to the hub, matching
  // the diagram's own fallback.
  const renderTargetSelect = (system: TechStackSystem) => {
    const targetOptions = namedSystems.filter((s) => s.id !== system.id);
    const flowTarget = system.integration?.flowTarget;
    const targetValue =
      flowTarget && targetOptions.some((s) => s.id === flowTarget) ? flowTarget : TECH_STACK_HUB_ID;
    return (
      <label className="tech-stack-editor__control">
        <span className="tech-stack-editor__control-label">Target</span>
        <Select
          aria-label="Integration flow target"
          value={targetValue}
          size="sm"
          disabled={disabled}
          onValueChange={(value) => setFlowTarget(system, value)}
        >
          <SelectItem value={TECH_STACK_HUB_ID}>{hubName}</SelectItem>
          {targetOptions.map((s) => (
            <SelectItem key={s.id} value={s.id}>
              {s.name}
            </SelectItem>
          ))}
        </Select>
      </label>
    );
  };

  return (
    <div className="tech-stack-editor">
      {TECH_STACK_CATEGORIES.map((category) => {
        const rows = systems.filter((system) => system.category === category.id);
        return (
          <div className="tech-stack-editor__category" key={category.id}>
            <div className="tech-stack-editor__category-head">
              <span className="tech-stack-editor__category-label">{category.label}</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="tech-stack-editor__add"
                disabled={disabled}
                onClick={() => addSystem(category.id)}
              >
                Add
              </Button>
            </div>

            {rows.length === 0 ? (
              <p className="tech-stack-editor__empty">No technologies added.</p>
            ) : (
              rows.map((system) => (
                <div className="tech-stack-editor__row" key={system.id}>
                  {/* Row 1: tool name + plan + scope (labeled); delete pinned to the far right. */}
                  <div className="tech-stack-editor__line tech-stack-editor__line--head">
                    <label className="tech-stack-editor__control tech-stack-editor__control--name">
                      <span className="tech-stack-editor__control-label">Tool</span>
                      <AutocompleteField
                        ariaLabel={`${category.label} technology name`}
                        placeholder="Tool name"
                        value={system.name}
                        options={optionsByCategory[category.id] ?? []}
                        size="compact"
                        disabled={disabled}
                        onChange={(value) => setName(system, value)}
                      />
                    </label>
                    <label className="tech-stack-editor__control">
                      <span className="tech-stack-editor__control-label">Plan</span>
                      <Select
                        aria-label={`${category.label} plan`}
                        value={system.plan}
                        size="sm"
                        disabled={disabled}
                        onValueChange={(value) => setPlan(system, value as TechStackPlan)}
                      >
                        {TECH_STACK_PLANS.map((plan) => (
                          <SelectItem key={plan} value={plan}>
                            {planLabel(plan)}
                          </SelectItem>
                        ))}
                      </Select>
                    </label>
                    <label className="tech-stack-editor__control">
                      <span className="tech-stack-editor__control-label">Scope</span>
                      <SegmentedControl<'in' | 'out'>
                        ariaLabel={`${category.label} scope`}
                        size="compact"
                        disabled={disabled}
                        value={system.inScope ? 'in' : 'out'}
                        onChange={(value) => setInScope(system, value === 'in')}
                        options={[
                          { value: 'in', label: 'In scope' },
                          { value: 'out', label: 'Out of scope' },
                        ]}
                      />
                    </label>
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      className="tech-stack-editor__delete"
                      disabled={disabled}
                      onClick={() => requestDelete(system)}
                    >
                      Delete
                    </Button>
                  </div>

                  {/* Row 2 (in scope only): integration type / effort / direction. */}
                  {system.inScope ? (
                    <div className="tech-stack-editor__line tech-stack-editor__line--controls">
                      <label className="tech-stack-editor__control">
                        <span className="tech-stack-editor__control-label">Integration</span>
                        <SegmentedControl<IntegrationType>
                          ariaLabel="Integration type"
                          size="compact"
                          disabled={disabled}
                          value={system.integration?.type ?? 'standard'}
                          onChange={(value) => setIntegrationType(system, value)}
                          options={[
                            { value: 'standard', label: 'Standard' },
                            { value: 'custom', label: 'Custom' },
                          ]}
                        />
                      </label>
                      <label className="tech-stack-editor__control tech-stack-editor__control--effort">
                        <span className="tech-stack-editor__control-label">Effort (hours)</span>
                        <NumberInput
                          min={0}
                          aria-label="Integration effort hours"
                          value={String(system.integration?.hours ?? 0)}
                          size="compact"
                          disabled={disabled}
                          onChange={(event) =>
                            setHours(system, Number(event.target.value.replace(/,/g, '')))
                          }
                        />
                      </label>
                      <label className="tech-stack-editor__control">
                        <span className="tech-stack-editor__control-label">Direction</span>
                        <Select
                          aria-label="Integration direction"
                          value={system.integration?.direction ?? 'inbound'}
                          size="sm"
                          disabled={disabled}
                          onValueChange={(value) =>
                            setDirection(system, value as IntegrationDirection)
                          }
                        >
                          {INTEGRATION_DIRECTIONS.map((direction) => (
                            <SelectItem key={direction} value={direction}>
                              {integrationDirectionLabel(direction)}
                            </SelectItem>
                          ))}
                        </Select>
                      </label>
                      {renderTargetSelect(system)}
                    </div>
                  ) : null}

                  {/* Row 3: notes (in scope only), full-width textarea. */}
                  {system.inScope ? (
                    <label className="tech-stack-editor__control tech-stack-editor__control--notes">
                      <span className="tech-stack-editor__control-label">Notes (optional)</span>
                      <TextArea
                        aria-label="Integration notes"
                        placeholder="Notes"
                        rows={2}
                        value={system.integration?.notes ?? ''}
                        disabled={disabled}
                        onChange={(event) => setNotes(system, event.target.value)}
                      />
                    </label>
                  ) : null}
                </div>
              ))
            )}
          </div>
        );
      })}

      <Dialog.Root
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) closeDelete();
        }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="dialog-overlay" />
          <Dialog.Content className="dialog-content tech-stack-editor__dialog">
            <Dialog.Title className="dialog-title">Delete technology</Dialog.Title>
            <Dialog.Description className="dialog-description">
              This removes <strong>{deleteTarget?.name}</strong> and its integration from the
              estimate and the systems landscape. Type the tool name to confirm.
            </Dialog.Description>
            <TextField
              aria-label="Type the tool name to confirm deletion"
              placeholder={deleteTarget?.name ?? ''}
              value={confirmText}
              size="comfortable"
              onChange={(event) => setConfirmText(event.target.value)}
            />
            <div className="tech-stack-editor__dialog-actions">
              <Button type="button" variant="secondary" size="sm" onClick={closeDelete}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                disabled={confirmText.trim() !== (deleteTarget?.name.trim() ?? '')}
                onClick={confirmDelete}
              >
                Delete technology
              </Button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}
